import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { StrategySwitch } from "@/components/StrategySwitch";
import { EngineSwitch } from "@/components/EngineSwitch";
import { EngineDayLog } from "@/components/EngineDayLog";
import { DailySummary } from "@/components/DayBits";
import { KiteBook } from "@/components/KiteBook";
import { Play, Square, OctagonX } from "lucide-react";
import { api, errMsg, inr, px, sign } from "@/lib/api";
import Pipeline from "@/components/Pipeline";
import KitePanel from "@/components/KitePanel";
import CandleChart from "@/components/CandleChart";
import { EventLog, Side, Stat, TradesTable } from "@/components/Bits";
import { stockChartProps } from "@/components/DayReplay";
import { useKiteStatus } from "@/components/Shell";

const MODES = [
  { id: "signals", label: "Signals", desc: "tickets only" },
  { id: "paper", label: "Paper", desc: "simulated orders" },
  { id: "trade", label: "Trade", desc: "real orders, Y each" },
];

export const Seg = ({ value, onChange, options, testId }) => (
  <div className="grid gap-1 p-1 rounded-md bg-[#0B111D] border border-[#1E293B]" style={{ gridTemplateColumns: `repeat(${options.length},1fr)` }} data-testid={testId}>
    {options.map((o) => (
      <button key={o.id} onClick={() => onChange(o.id)} data-testid={`${testId}-${o.id}`}
        className={`py-1.5 rounded text-xs font-medium transition-colors ${value === o.id ? "bg-[#1A2235] text-cyan-300" : "text-slate-400 hover:text-slate-100"}`}>
        {o.label}{o.desc && <span className="block text-[10px] text-slate-500 font-normal">{o.desc}</span>}
      </button>
    ))}
  </div>
);

const saved = (k, d) => localStorage.getItem(`lvrc-${k}`) || d;

function Controls({ running, onStart, onStop, lastSource, lastMode, enabled }) {
  const [kite] = useKiteStatus();
  // your choice is kept on this device; while a run is active the selector shows that run's mode and is locked
  const [mode, setModeRaw] = useState(() => saved("mode", "paper"));
  const setMode = (m) => { localStorage.setItem("lvrc-mode", m); setModeRaw(m); };
  useEffect(() => { if (running && lastMode) { localStorage.setItem("lvrc-mode", lastMode); setModeRaw(lastMode); } }, [running, lastMode]);
  const [source, setSource] = useState("replay");
  const [bias, setBias] = useState("AUTO");
  const [date, setDate] = useState("");
  const [speed, setSpeed] = useState(1.5);
  const [auto, setAuto] = useState(true);
  useEffect(() => { api.get("/settings").then((r) => setAuto(!!r.data.auto_entry)).catch(() => {}); }, []);
  useEffect(() => { setSource(lastSource === "kite" ? "kite" : lastSource ? "replay" : (kite?.logged_in ? "kite" : "replay")); }, [kite?.logged_in, lastSource]);
  const start = () => {
    if (mode === "trade" && source === "kite" && !window.confirm(auto ? "Start FULLY AUTOMATIC trade mode on your real Zerodha account? Entries will be placed without asking you." : "Start TRADE mode on your real Zerodha account? Each entry still needs your Y.")) return;
    onStart({ mode, source, bias_override: bias === "AUTO" ? null : bias, replay_date: date || null, speed: Number(speed) });
  };
  return (
    <div className="panel p-4 space-y-3" data-testid="live-controls">
      <div className="label-xs">Run mode</div>
      <Seg value={mode} onChange={running ? () => toast.info("Stop the current run to change its mode") : setMode} options={MODES.map((m) => (m.id === "trade" && auto ? { ...m, desc: "real orders, auto" } : m))} testId="mode-selector" />
      <div className="label-xs pt-1">Data</div>
      <Seg value={source} onChange={setSource} testId="source-selector"
        options={[{ id: "kite", label: "Kite live" }, { id: "replay", label: "Past day replay" }]} />
      {source === "replay" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] text-slate-400">Replay day
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="replay-date-input"
              className="mt-1 w-full h-9 rounded-md bg-[#0B111D] border border-[#1E293B] px-2 text-xs num text-slate-200" />
          </label>
          <label className="text-[11px] text-slate-400">Sec / candle
            <input type="number" step="0.5" min="0.2" max="10" value={speed} onChange={(e) => setSpeed(e.target.value)} data-testid="replay-speed-input"
              className="mt-1 w-full h-9 rounded-md bg-[#0B111D] border border-[#1E293B] px-2 text-xs num text-slate-200" />
          </label>
          <p className="col-span-2 text-[11px] text-slate-400" data-testid="replay-data-note">
            {kite?.logged_in
              ? "Uses that day's REAL Kite 5-min candles — same A/D, sector and stocks the live run would pick. No real orders are sent."
              : <span className="text-amber-300/90">Zerodha not connected → SAMPLE (synthetic) candles; sector and stocks won't match the real day. Log in to Zerodha for a real replay.</span>}
          </p>
        </div>
      )}
      <div className="label-xs pt-1">Bias (step 1)</div>
      <Seg value={bias} onChange={setBias} testId="bias-override"
        options={[{ id: "AUTO", label: "A/D" }, { id: "LONG", label: "Long" }, { id: "SHORT", label: "Short" }, { id: "NONE", label: "None" }]} />
      {mode === "trade" && (
        <p data-testid="entry-mode-note" className={`text-xs ${auto ? "text-amber-300" : "text-slate-400"}`}>
          {auto ? "Fully automatic: entries are placed without your Y (change in Settings → 6 · Order)." : "Each entry waits for your Y."}
        </p>
      )}
      {running ? (
        <button onClick={onStop} data-testid="live-stop-btn" className="w-full h-12 rounded-md border border-slate-600 text-slate-200 hover:bg-[#1A2235] font-semibold flex items-center justify-center gap-2">
          <Square size={16} /> Stop run
        </button>
      ) : (
        <button onClick={start} disabled={enabled === false} data-testid="live-start-btn" className="w-full h-12 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-40">
          <Play size={16} /> Start {MODES.find((m) => m.id === mode).label}
        </button>
      )}
    </div>
  );
}

export function KillSwitch({ onKill, disabled, testId = "kill-switch-btn" }) {
  const [armed, setArmed] = useState(false);
  const t = useRef();
  const click = () => {
    if (!armed) { setArmed(true); t.current = setTimeout(() => setArmed(false), 3000); return; }
    clearTimeout(t.current); setArmed(false); onKill();
  };
  return (
    <button onClick={click} disabled={disabled} data-testid={testId}
      className={`w-full h-14 rounded-md font-bold tracking-wide flex items-center justify-center gap-2 transition-colors disabled:opacity-40 ${armed ? "bg-red-500 text-white" : "border-2 border-red-500/70 text-red-400 hover:bg-red-500/10"}`}>
      <OctagonX size={18} /> {armed ? "TAP AGAIN TO KILL" : "KILL SWITCH"}
    </button>
  );
}

export function Ticket({ t, mode, onConfirm, targetLabel = "2R" }) {
  const awaiting = t.awaiting;
  return (
    <div className={`panel p-4 rise ${awaiting ? "border-amber-500/60 pulse-amber" : ""}`} data-testid={`ticket-${t.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="num text-base text-slate-100 font-semibold">{t.sym}</span><Side side={t.side} />
        <span className="ml-auto label-xs">{awaiting ? "awaiting Y" : mode === "signals" ? "ticket" : "order live"} · {t.left} candles left</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 text-xs num">
        {[["Trigger", t.trigger], ["Limit", t.limit], ["Stop", t.stop], [targetLabel, t.target], ["Qty", t.qty], ["Value", t.value ? `₹${(t.value / 100000).toFixed(2)} L` : "—"], ["Signal", t.sig?.t]].map(([k, v]) => (
          <div key={k}><div className="label-xs">{k}</div><div className="text-slate-100">{typeof v === "number" && k !== "Qty" ? px(v) : v}</div></div>
        ))}
      </div>
      {t.margin && (
        <div className={`mt-2 text-xs num flex flex-wrap gap-x-4 gap-y-1 ${t.margin.ok === false ? "text-red-400" : "text-slate-400"}`} data-testid={`ticket-margin-${t.id}`}>
          <span>Margin needed <b className="text-slate-100">{inr(t.margin.need)}</b></span>
          {t.margin.available != null && <span>Available <b className={t.margin.ok ? "text-slate-100" : "text-red-400"}>{inr(t.margin.available)}</b></span>}
          <span className="text-slate-500">{t.margin.source}</span>
          {t.margin.ok === false && <span className="font-semibold" data-testid={`ticket-margin-short-${t.id}`}>Margin short — Zerodha will reject this order</span>}
        </div>
      )}
      {awaiting && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={() => onConfirm(t.id, true)} data-testid={`ticket-confirm-y-${t.id}`} className="h-14 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-lg font-bold">Y · Place</button>
          <button onClick={() => onConfirm(t.id, false)} data-testid={`ticket-confirm-n-${t.id}`} className="h-14 rounded-md border border-slate-600 text-slate-200 text-lg font-bold hover:bg-[#1A2235]">N · Skip</button>
        </div>
      )}
    </div>
  );
}

function StockCard({ s, st }) {
  return (
    <div className="panel p-3" data-testid={`stock-card-${s.sym}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="num font-semibold text-slate-100">{s.sym}</span>{s.fut && <span className="text-[10px] text-violet-300 num" data-testid={`stock-fut-${s.sym}`}>{s.fut}</span>}
        <span className={`num text-xs ${sign(s.pct)}`}>{s.pct > 0 ? "+" : ""}{s.pct}%</span>
        <span className="ml-auto text-[11px] text-cyan-300" data-testid={`stock-status-${s.sym}`}>{s.status || "waiting"}</span>
      </div>
      <div className="flex gap-3 text-[11px] num text-slate-400 mb-1">
        <span>OR {px(s.or_low)}–{px(s.or_high)}</span><span data-testid={`stock-ltp-${s.sym}`}>LTP {px(s.last ?? s.close)}</span><span>lot {s.lot}</span>
      </div>
      <CandleChart {...stockChartProps(s, st.events || [], st.trades || [], s.forming ? [...(s.candles || []), s.forming] : s.candles || [])} testId={`stock-chart-${s.sym}`} />
    </div>
  );
}

const pnlSub = (st) => {
  const closed = st.trades?.length || 0, open = st.positions?.length || 0;
  const ev = st.events || [];
  const sigs = ev.filter((e) => e.kind === "signal" || e.kind === "shift").length;
  const parts = [`${closed} closed`, `${open} open${open ? " (before charges)" : ""}`];
  if (!closed && !open) {
    if (st.tickets?.length) parts.push("order waiting for trigger");
    else if (sigs) parts.push(`${sigs} signal${sigs > 1 ? "s" : ""}, none filled (expired/cancelled)`);
    else parts.push("no signal yet");
  }
  return parts.join(" · ");
};

const istHMS = (ms) => new Date(ms).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour12: false });

// Kite live: real IST time from the server clock (ticks every second) + how fresh the engine heartbeat is
function RunClock({ st, running, now, hb }) {
  const [tick, setTick] = useState(Date.now());
  const [off, setOff] = useState(0);
  useEffect(() => { if (now) setOff(now * 1000 - Date.now()); }, [now]);
  useEffect(() => { const i = setInterval(() => setTick(Date.now()), 1000); return () => clearInterval(i); }, []);
  if (st.source !== "kite" || !running) {
    return <Stat label={st.source === "kite" ? "Clock (IST)" : "Replay clock"} value={st.clock} sub={st.source === "kite" ? "run ended" : `simulated · ${st.date}`} testId="stat-clock" />;
  }
  const srv = tick + off;
  const age = hb ? Math.max(0, Math.round((srv - hb * 1000) / 1000)) : null;
  return <Stat label="Clock (IST)" value={istHMS(srv)} cls={age !== null && age > 20 ? "text-amber-300" : "text-slate-100"}
    sub={age === null ? "" : age > 20 ? `engine silent ${age}s — recovering` : `engine live · candles to ${st.clock}`} testId="stat-clock" />;
}

export default function Dashboard() {
  const [on, setOn] = useState(null);
  const [data, setData] = useState({ running: false, state: null });
  const seq = useRef(0);
  const shown = useRef(0);
  const busy = useRef(false);
  const load = useCallback((force) => {
    if (busy.current && !force) return Promise.resolve();
    busy.current = true;
    const n = ++seq.current;
    return api.get("/live/state").then((r) => {
      if (n < shown.current) return;  // drop stale replies that arrive late
      shown.current = n;
      setData((prev) => (JSON.stringify(prev) === JSON.stringify(r.data) ? prev : r.data));
    }).catch(() => {}).finally(() => { busy.current = false; });
  }, []);
  useEffect(() => { load(); const i = setInterval(() => load(), 1500); return () => clearInterval(i); }, [load]);
  const st = data.state;
  const intr = data.interrupted;
  const ackIntr = () => api.post(`/live/interrupted/${intr.id}/ack`).then(() => load(true));
  const seenAlerts = useRef(null);
  useEffect(() => {
    const alerts = (st?.events || []).filter((e) => e.kind === "margin");
    const key = (e) => `${st.id}|${e.t}|${e.msg}`;
    if (seenAlerts.current === null) { seenAlerts.current = new Set(alerts.map(key)); return; }
    alerts.forEach((e) => { if (!seenAlerts.current.has(key(e))) { seenAlerts.current.add(key(e)); toast.error(e.msg, { duration: 10000 }); } });
  }, [st]);

  const act = async (fn, ok) => { try { const r = await fn(); if (r?.data?.state !== undefined) { shown.current = ++seq.current; setData(r.data); } else load(true); ok && toast.success(ok); } catch (e) { toast.error(errMsg(e)); } };
  const onStart = (body) => act(() => api.post("/live/start", body).then(() => api.get("/live/state")), "Run started");
  const onStop = () => act(() => api.post("/live/stop"), "Run stopped");
  const onKill = () => act(() => api.post("/live/kill"), "Kill switch: all cancelled & squared off");
  const onConfirm = (order_id, accept) => act(() => api.post("/live/confirm", { order_id, accept }), accept ? "Entry confirmed" : "Ticket skipped");

  const awaiting = st?.tickets?.find((t) => t.awaiting);
  useEffect(() => {
    const h = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (awaiting && (e.key === "y" || e.key === "Y")) onConfirm(awaiting.id, true);
      if (awaiting && (e.key === "n" || e.key === "N")) onConfirm(awaiting.id, false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4">
      <aside className="space-y-3 order-2 lg:order-1">
        <StrategySwitch id="lvrc" label="LVRC strategy" onChange={setOn} />
        {on === false && <p className="text-[11px] text-amber-300" data-testid="lvrc-off-note">LVRC is OFF — it won't start.</p>}
        <Controls running={data.running || !!st?.resuming} onStart={onStart} onStop={onStop} lastSource={st?.source} lastMode={st?.mode} enabled={on} />
        <div className="hidden md:block"><KillSwitch onKill={onKill} disabled={!st || st.phase === "done"} /></div>
        <KitePanel />
      </aside>
      <section className="space-y-3 order-1 lg:order-2 min-w-0">
        <EngineSwitch />
        <DailySummary />
        {st?.resuming && (
          <div className="panel p-4 border-amber-500/60 bg-amber-500/10" data-testid="resuming-alert">
            <p className="text-sm font-semibold text-amber-300">The app restarted — resuming your {st.mode?.toUpperCase()} run automatically…</p>
            <p className="text-xs text-slate-300 mt-1">It rebuilds today's state from Kite candles and re-links your Zerodha orders. This takes up to a minute.</p>
          </div>
        )}
        {intr && !st?.resuming && !data.running && (
          <div className="panel p-4 border-red-500/60 bg-red-500/10" data-testid="interrupted-alert">
            <p className="text-sm font-semibold text-red-300">The app restarted during your {intr.mode?.toUpperCase()} run on {intr.date}. That run is no longer being managed.</p>
            <p className="text-xs text-slate-300 mt-1">
              {intr.mode === "trade"
                ? `Open Kite and check for pending orders or open positions (${intr.positions} open, ${intr.tickets} pending when it stopped). Stops and exits are NOT being managed by the app now.`
                : "No real orders were sent. Start a new run if the market is still open."}
            </p>
            {intr.mode === "trade" && intr.source === "kite" && <KiteBook testId="interrupted-kite-book" onClean={ackIntr} />}
            <div className="mt-3 flex gap-3 text-xs">
              <a href={`/history/${intr.id}`} className="text-cyan-300 hover:underline" data-testid="interrupted-view-link">View run</a>
              <button onClick={ackIntr} className="text-slate-300 hover:text-white" data-testid="interrupted-dismiss-btn">I've checked — dismiss</button>
            </div>
          </div>
        )}
        {!st ? (
          <div className="panel p-8 grid-bg" data-testid="live-empty">
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-100 mb-2">No run today yet</h1>
            <p className="text-sm text-slate-400 max-w-lg">Choose a mode and press Start before 09:20. The engine reads A/D at 09:20, picks the sector and top 4 stocks at 09:35, then takes the lowest-volume reversal candle across them after the range break — one trade at a time.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="run-header">
              <span className="num text-slate-400">{st.date}</span>
              <span className="px-2 py-0.5 rounded border border-cyan-500/40 text-cyan-300 uppercase" data-testid="run-mode">{st.mode}</span>
              <span className="px-2 py-0.5 rounded border border-[#334155] text-slate-300">{st.source === "kite" ? "Kite live" : st.source === "kite_replay" ? "Kite history replay" : "SAMPLE replay (synthetic)"}</span>
              <span className="px-2 py-0.5 rounded border border-[#334155] text-slate-300">orders: {st.broker}</span>
              <span className={`px-2 py-0.5 rounded border ${st.status === "running" ? "border-amber-500/50 text-amber-300" : "border-[#334155] text-slate-400"}`} data-testid="run-status">{st.status}</span>
              {st.loading && st.phase !== "done" && <span className="text-amber-300" data-testid="run-loading">{st.loading}</span>}
              {st.reason && <span className="text-slate-400" data-testid="run-reason">{st.reason}</span>}
              {st.settings?.instrument === "futures" && <span className="px-2 py-0.5 rounded border border-violet-500/40 text-violet-300" data-testid="run-instrument">FUTURES</span>}
              {st.settings?.universe === "nifty50" && <span className="px-2 py-0.5 rounded border border-[#334155] text-slate-300">Nifty 50</span>}
              {st.updated && <span className="ml-auto text-slate-500 num" data-testid="run-updated">{data.running && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5 align-middle" />}updated {st.updated}</span>}
            </div>
            <Pipeline state={st} />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <RunClock st={st} running={data.running} now={data.now} hb={data.hb} />
              <Stat label="A/D ratio" value={st.ad ? st.ad.ratio : "—"} sub={st.ad ? `${st.ad.advances}↑ ${st.ad.declines}↓ · ${st.ad.source}` : "reads at 09:20"} testId="stat-ad" />
              <Stat label="Bias" value={st.bias || (st.phase === "wait_recheck" ? "…" : "—")} cls={st.bias === "LONG" ? "up" : st.bias === "SHORT" ? "down" : "text-slate-300"}
                sub={st.bias_override ? "your override" : st.ad_recheck ? `09:30 re-check ${st.ad_recheck.ratio} (${st.ad_recheck.advances}↑ ${st.ad_recheck.declines}↓)` : st.phase === "wait_recheck" ? "re-checking at 09:30" : ""} testId="stat-bias" />
              <Stat label="Sector" value={st.sector || "—"} cls="text-slate-100 text-base" testId="stat-sector" />
              <Stat label="Day P&L" value={inr(st.net)} cls={sign(st.net)} sub={pnlSub(st)} testId="stat-pnl" />
            </div>
            {!!st.tickets?.length && (
              <div className="space-y-2" data-testid="tickets-panel">
                {st.tickets.map((t) => <Ticket key={t.id} t={t} mode={st.mode} onConfirm={onConfirm} />)}
              </div>
            )}
            {!!st.stocks?.length && (
              <div className="grid xl:grid-cols-3 md:grid-cols-2 gap-3" data-testid="stocks-panel">
                {st.stocks.map((s) => <StockCard key={s.sym} s={s} st={st} />)}
              </div>
            )}
            {!!st.positions?.length && (
              <div className="panel p-3" data-testid="positions-panel">
                <div className="label-xs mb-2">Open positions</div>
                {st.positions.map((p) => (
                  <div key={p.sym} className="flex flex-wrap gap-x-4 gap-y-1 text-xs num py-1.5 border-b border-[#1E293B]/60" data-testid={`position-${p.sym}`}>
                    <span className="text-slate-100 font-semibold">{p.sym}</span><Side side={p.side} />
                    <span>{p.rem}/{p.qty} @ {px(p.entry)}</span><span className="down">SL {px(p.stop)}</span><span className="up">2R {px(p.target)}</span>
                    <span>LTP {px(p.last)}</span><span className={sign(p.unrealized + p.booked)}>{inr(p.unrealized + p.booked)}</span>
                    {p.kite && <span className={p.kite === "filled at Kite" ? "text-cyan-300" : "text-amber-300 font-semibold"} data-testid={`position-kite-${p.sym}`}>Zerodha: {p.kite}</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <div className="panel p-3 min-w-0"><div className="label-xs mb-2">Closed trades</div><TradesTable trades={st.trades} testId="live-trades" /></div>
              <div className="panel p-3 min-w-0"><div className="label-xs mb-2">Live timeline</div><EventLog events={st.events} testId="live-timeline" /></div>
            </div>
          </>
        )}
        <EngineDayLog />
      </section>
      {st && st.phase !== "done" && (
        <div className="md:hidden fixed inset-x-0 bottom-[58px] z-30 border-t border-[#1E293B] bg-[#090D16]/95 backdrop-blur-xl p-2 space-y-2" data-testid="mobile-action-bar">
          {awaiting && (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => onConfirm(awaiting.id, true)} data-testid="mobile-confirm-y" className="h-14 rounded-md bg-cyan-500 text-slate-950 font-bold">Y · {awaiting.sym} @ {px(awaiting.trigger)}</button>
              <button onClick={() => onConfirm(awaiting.id, false)} data-testid="mobile-confirm-n" className="h-14 rounded-md border border-slate-600 text-slate-200 font-bold">N · Skip</button>
            </div>
          )}
          <KillSwitch onKill={onKill} testId="mobile-kill-switch-btn" />
        </div>
      )}
      {st && st.phase !== "done" && <div className={`md:hidden order-3 ${awaiting ? "h-36" : "h-20"}`} />}
    </div>
  );
}
